import React from 'react';
import { Alert, Box, Flex, IconButton, Spinner } from 'pounceV2';
import type { SxProps, Theme } from '@mui/material';
import { AISummarizeKind, OutputLength } from 'Generated/schema';
import ErrorBoundary from 'Components/ErrorBoundary';
import { RISK_EXPLAIN_PROMPT } from 'Components/AI/EnrichedAIResponseV3';
import type { Attachment } from 'Components/AIExplainLogEvents/hooks/useAIAttachments';
import { DEFAULT_SCROLL_THRESHOLD } from 'Pages/AI/hooks/useAutoScroll';
import MessageListV3 from './MessageListV3';
import ConversationInputV3 from './ConversationInputV3';
import { STREAMS_PAGE_SIZE } from './hooks/useConversationMessagingV3';
import type { UseConversationMessagingV3Return } from './hooks/useConversationMessagingV3';

export type ConversationPanelV3Props = Pick<
  UseConversationMessagingV3Return,
  | 'messages'
  | 'hasMore'
  | 'loadMore'
  | 'isLoadingMore'
  | 'forceScrollToBottom'
  | 'isStreaming'
  | 'isCreating'
  | 'isContinuing'
  | 'isInitialLoad'
  | 'isCancelled'
  | 'cancelStream'
  | 'error'
> & {
  handleSendMessage: (userPrompt: string, outputLength: OutputLength) => void;
  conversationId: string | null;
  kind?: AISummarizeKind;
  placeholder?: string;
  onAttachFile?: (file: File) => void;
  isAttachDisabled?: boolean;
  isUploading?: boolean;
  attachments?: Attachment[];
  onRemoveAttachment?: (id: string) => void;
  inputContainerSx?: SxProps<Theme>;
  initialScrollBehavior?: 'top' | 'bottom';
  /** Optional row rendered as the first item inside the Next Steps card (before AI prompts). */
  recommendedActionsHeader?: React.ReactNode;
  /** Optional row rendered below the last finished AI message (outside the Next Steps card). */
  recommendedActionsRow?: React.ReactNode;
  /** Optional control rendered in the composer's left action group (e.g. the privacy selector). */
  leadingComposerControl?: React.ReactNode;
};

/**
 * Self-contained messaging panel: scroll area + message list + input.
 *
 * Designed for the controlled pattern — call useConversationMessagingV3 in the
 * consumer and spread (or pick) its return value into this component.
 *
 * The consumer retains full access to hook state (isStreaming, cancelStream,
 * conversation metadata, etc.) for its own header/toolbar needs.
 *
 * Example:
 * ```tsx
 * const messaging = useConversationMessagingV3({ conversationId, isPrivate, onConversationCreated });
 * return <ConversationPanelV3 {...messaging} placeholder="Ask about this alert..." />;
 * ```
 */
const ConversationPanelV3: React.FC<ConversationPanelV3Props> = ({
  conversationId,
  kind,
  messages,
  hasMore,
  loadMore,
  isLoadingMore,
  forceScrollToBottom,
  isStreaming,
  isCreating,
  isContinuing,
  isInitialLoad,
  isCancelled,
  handleSendMessage,
  cancelStream,
  error,
  placeholder,
  onAttachFile,
  isAttachDisabled,
  isUploading,
  attachments,
  onRemoveAttachment,
  inputContainerSx,
  initialScrollBehavior = 'bottom',
  recommendedActionsHeader,
  recommendedActionsRow,
  leadingComposerControl,
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isInputDisabled = isCreating || isContinuing || isStreaming || isInitialLoad;
  const isThinking = isCreating || isContinuing || isStreaming;

  // Show a scroll-to-bottom button when the user has scrolled up
  const [isNearBottom, setIsNearBottom] = React.useState(true);

  // Scroll to bottom when messages first appear (e.g. after switching conversations).
  // The forceScrollToBottom trigger in the hook fires before messages are rendered;
  // this catches the transition from empty → loaded regardless of cache vs network timing.
  // When initialScrollBehavior is 'top', skip this so the conversation starts at the top.
  // Also recalculates isNearBottom so the scroll-to-bottom button appears immediately when needed.
  const prevMessagesLengthRef = React.useRef(0);

  // Reset scroll position when switching conversations. The 0→N effect below only handles
  // the non-cached case. When Apollo serves cached data, messages go directly N→M without
  // passing through 0, so we must scroll here for the cached case too.
  React.useEffect(() => {
    prevMessagesLengthRef.current = 0;
    const container = scrollContainerRef.current;
    if (container && messages.length > 0 && initialScrollBehavior === 'top') {
      container.scrollTop = 0;
    }
    // messages.length and initialScrollBehavior are intentionally read from closure;
    // this effect must only run on conversation change, not on every message append.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  React.useEffect(() => {
    if (prevMessagesLengthRef.current === 0 && messages.length > 0) {
      const container = scrollContainerRef.current;
      if (container && initialScrollBehavior === 'bottom') {
        container.scrollTop = container.scrollHeight;
      }

      if (container) {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        setIsNearBottom(distanceFromBottom <= DEFAULT_SCROLL_THRESHOLD);
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, initialScrollBehavior]);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setIsNearBottom(distanceFromBottom <= DEFAULT_SCROLL_THRESHOLD);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
    // scrollContainerRef is intentionally omitted: it's a stable ref object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScrollToBottom = React.useCallback(() => {
    scrollContainerRef.current?.scrollTo({
      top: scrollContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  const handleRiskExplain = React.useCallback(() => {
    handleSendMessage(RISK_EXPLAIN_PROMPT, OutputLength.Large);
  }, [handleSendMessage]);

  const handlePromptClick = React.useCallback(
    (prompt: string) => handleSendMessage(prompt, OutputLength.Medium),
    [handleSendMessage],
  );

  return (
    <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
      <Box flex={1} minHeight={0} position="relative" display="flex" flexDirection="column">
        <Box
          ref={scrollContainerRef}
          flex={1}
          overflowY="auto"
          overflowX="hidden"
          minHeight={0}
          width="100%"
        >
          {isInitialLoad && !messages.length && !error && !isCreating && !isContinuing && (
            <Flex justify="center" align="center" height="100%" pt={10}>
              <Spinner size="medium" />
            </Flex>
          )}
          {Boolean(conversationId) && !messages.length && error && (
            <Box p={6} width="100%">
              <Alert
                variant="error"
                title="Failed to load conversation"
                description={error.message || 'An unexpected error occurred.'}
              />
            </Box>
          )}
          {messages.length > 0 && error && (
            <Box px={6} pt={4} width="100%">
              <Alert
                variant="error"
                title="Failed to refresh conversation"
                description="Some recent changes may not be visible."
              />
            </Box>
          )}
          {messages.length > 0 && (
            <ErrorBoundary>
              {/* onSearchRequest intentionally omitted — Kendra entity linking not supported in
                  conversational mode. ConversationInput has no mechanism to accept pre-filled
                  queries from inline code clicks. */}
              <MessageListV3
                messages={messages}
                forceScrollToBottom={forceScrollToBottom}
                scrollContainerRef={scrollContainerRef}
                hasMore={hasMore}
                loadMore={loadMore}
                isLoadingMore={isLoadingMore && messages.length >= STREAMS_PAGE_SIZE}
                onRiskExplain={handleRiskExplain}
                isCancelled={isCancelled}
                onPromptClick={handlePromptClick}
                isThinking={isThinking}
                recommendedActionsHeader={recommendedActionsHeader}
                recommendedActionsRow={recommendedActionsRow}
              />
            </ErrorBoundary>
          )}
        </Box>
        <Flex
          position="absolute"
          bottom={16}
          left={0}
          right={0}
          justifyContent="center"
          zIndex={1}
          opacity={isNearBottom || messages.length === 0 ? 0 : 1}
          pointerEvents={!isNearBottom && messages.length > 0 ? 'auto' : 'none'}
          transition="opacity 200ms ease"
        >
          <IconButton
            icon="chevron-down"
            aria-label="Scroll to bottom"
            variant="primary"
            size="medium"
            onClick={handleScrollToBottom}
          />
        </Flex>
      </Box>
      <Box sx={inputContainerSx}>
        <ConversationInputV3
          onSubmit={handleSendMessage}
          kind={kind}
          isLoading={isStreaming}
          disabled={isInputDisabled}
          cancelPrompt={cancelStream}
          placeholder={placeholder}
          onAttachFile={onAttachFile}
          isAttachDisabled={isAttachDisabled}
          isUploading={isUploading}
          attachments={attachments}
          onRemoveAttachment={onRemoveAttachment}
          leadingControl={leadingComposerControl}
        />
      </Box>
    </Box>
  );
};

export default ConversationPanelV3;
